import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';

interface LegendItemProps {
  disabled?: boolean;
  label: string;
  color: string;
  isVisible: boolean;
  onClick: () => void;
}

interface Props<T extends readonly string[]> {
  colors: Record<T[number], string>;
  labels: T;
  labelsVisible: Set<T[number]>;
  legendDirection: 'row' | 'column';
  onLegendItemClick: (label: string) => void;
}

const AnalyticsChartLegend = <T extends readonly string[]>({
  colors,
  labels,
  labelsVisible,
  legendDirection,
  onLegendItemClick
}: Props<T>) => {
  const { theme } = useReduxState();

  const LegendItem = ({
    disabled,
    label,
    color,
    isVisible,
    onClick
  }: LegendItemProps) => (
    <button className="legend-item" onClick={disabled ? undefined : onClick}>
      <div
        className="legend-color-box"
        style={{
          backgroundColor: isVisible ? color : 'transparent',
          borderColor: color
        }}
      />

      <span className="legend-label">{label}</span>

      <style jsx>{`
        .legend-item {
          align-items: center;
          background-color: transparent;
          border: none;
          border-radius: ${theme.buttonBorderRadius};
          cursor: ${disabled ? 'default' : 'pointer'};
          display: flex;
          font-family: inherit;
          gap: 10px;
          min-width: 0;
          opacity: ${isVisible ? 1 : 0.5};
          padding: 3px 6px;
          text-align: left;
          transition: background-color 0.3s;
          white-space: nowrap;
          width: fit-content;
        }
        .legend-item:hover {
          background-color: ${disabled
            ? 'transparent'
            : theme.lightGreyLighter};
        }
        .legend-color-box {
          align-items: center;
          border: 2px solid;
          border-radius: 20px;
          color: white;
          display: inline-flex;
          flex-grow: 0;
          flex-shrink: 0;
          font-size: 0.9rem;
          justify-content: center;
          transition: background-color 0.3s;
          height: 12px;
          width: 12px;
        }
        .legend-label {
          color: ${theme.textColorLight};
          flex-grow: 1;
          flex-shrink: 1;
          font-size: 1.4rem;
          font-weight: 400;
          max-width: 300px;
          user-select: none;
          white-space: normal;
        }
      `}</style>
    </button>
  );

  return (
    <div className="legend">
      {labels.map((label) => (
        <LegendItem
          disabled={labelsVisible.size === 1 && labelsVisible.has(label)}
          key={label}
          label={label}
          color={colors[label]}
          isVisible={labelsVisible.has(label)}
          onClick={() => onLegendItemClick(label)}
        />
      ))}

      <style jsx>{`
        .legend {
          display: grid;
          flex-direction: column;
          gap: 5px 10px;
          grid-template-columns: ${legendDirection === 'row'
            ? 'repeat(auto-fit, minmax(220px, max-content))'
            : '1fr'};
        }
        @media (min-width: ${breakpoints.medium}px) {
          .legend {
            flex-direction: row;
          }
        }
      `}</style>
    </div>
  );
};
export default AnalyticsChartLegend;
